import { db } from "../../db";

// The link between an experiment and its endpoint lives only in the
// experiment (referenceId, variations[].blockId): the endpoint editor finds
// it by looking for the open experiment of the endpoint.

// Open: not archived yet (promoted or closed), so it still has its reference
export const isOpen = (experiment) =>
  Boolean(experiment?.reference && !experiment?.outcome);

// Started: saved once with Running on. From then on endpoint, pages, values,
// order, weights, strategy, assignment and start date don't change
export const isStarted = (experiment) => Boolean(experiment?.startedAt);

// The rows of the experiments table (MemoryDB), or the ones given
const rowsOf = (experiments) => {
  const rows = experiments ?? db.getTableRef("experiments");
  return rows ? [...rows.values()] : [];
};

// The old format (before the direct link): no variation points to a page.
// It serves nothing and locks nothing in the endpoint editor, but it still
// holds its reference: a new experiment on its endpoint waits until it is
// deleted
export const isLegacy = (experiment) =>
  !(experiment?.variations ?? []).some((variation) => variation?.blockId);

// The open experiment of the endpoint, or undefined. Experiments of the old
// format only with { legacy: true } (the reference they hold)
export function openExperimentOf(endpointId, experiments, { legacy } = {}) {
  if (!endpointId) return undefined;
  return rowsOf(experiments).find(
    (experiment) =>
      isOpen(experiment) &&
      experiment.referenceId === endpointId &&
      (legacy || !isLegacy(experiment))
  );
}

// The open experiment a page is linked to, with its variation:
// { experiment, variation, index } or undefined
export function experimentOfPage(blockId, experiments) {
  if (!blockId) return undefined;
  for (const experiment of rowsOf(experiments)) {
    if (!isOpen(experiment)) continue;
    const index = (experiment.variations ?? []).findIndex(
      (variation) => variation.blockId === blockId
    );
    if (index >= 0)
      return { experiment, variation: experiment.variations[index], index };
  }
  return undefined;
}

// The letter of a variation: A for the control, then B, C...
export const variationLetter = (index) => String.fromCharCode(65 + index);

// The canonical reference of an endpoint: the property in lower case, as
// builder-block looks it up (`<property>:<endpoint>`)
export function referenceOf(endpoint) {
  if (!endpoint) return undefined;
  const property = endpoint.propertyId
    ? db.peek("properties", endpoint.propertyId)
    : undefined;
  return property?.title
    ? `${property.title.toLowerCase()}:${endpoint.reference}`
    : endpoint.reference;
}

// An endpoint with active variants can't host an experiment: during the
// test Ares serves the pages of the experiment, without conditions
export const hasActiveVariants = (endpoint) =>
  Boolean(endpoint?.variants?.some((variant) => variant?.active));
